import React, { useEffect, useState } from 'react';
import { api, endpoints } from '../config/api';
import { Button, Alert, Spinner, Badge } from '../components/ui';
import DashboardCard from '../components/DashboardCard';
import useOverlayClose from '../hooks/useOverlayClose';
import { surveyLabel } from '../constants/signaturePlaceholders';
import { useT } from '../i18n';
import './Surveys.css';

const messages = {
  docTitle: { tr: 'Memnuniyet Anketi - Signature Manager', en: 'Satisfaction Survey - Signature Manager' },
  noResponses30: { tr: 'Son 30 günde yanıt yok.', en: 'No responses in the last 30 days.' },
  actionStatus: {
    tr: { open: 'Açık', in_progress: 'İşlemde', waiting: 'Beklemede', resolved: 'Çözüldü', closed: 'Kapandı' },
    en: { open: 'Open', in_progress: 'In progress', waiting: 'Waiting', resolved: 'Resolved', closed: 'Closed' },
  },
  actionPriority: {
    tr: { low: 'Düşük', medium: 'Orta', high: 'Yüksek' },
    en: { low: 'Low', medium: 'Medium', high: 'High' },
  },
  actionUpdateFailed: { tr: 'Aksiyon güncellenemedi: {msg}', en: 'Couldn’t update the action: {msg}' },
  analyticsFailed: { tr: 'Analitik alınamadı: {msg}', en: 'Couldn’t load analytics: {msg}' },
  title: { tr: 'Memnuniyet Anketi', en: 'Satisfaction Survey' },
  hint: {
    tr: () => <>Anketler <a href="/signatures">İmza Şablonları</a> sayfasındaki şablon düzenleyicisinden eklenir — bu sayfa yalnızca yanıt/istatistik takibi içindir. Bir şablona eklenen anket, o şablonun atandığı grup(lar)a otomatik uygulanır.</>,
    en: () => <>Surveys are added from the template editor on the <a href="/signatures">Signature Templates</a> page — this page is only for tracking responses and statistics. A survey added to a template applies automatically to the group(s) that template is assigned to.</>,
  },
  surveysTab: { tr: 'Anketler', en: 'Surveys' },
  actionsTab: { tr: 'Aksiyonlar', en: 'Actions' },
  error: { tr: 'Hata: {msg}', en: 'Error: {msg}' },
  analyticsTitle: { tr: '{name} — Analitik', en: '{name} — Analytics' },
  avgRating: { tr: 'Ortalama Puan', en: 'Average Rating' },
  totalResponses: { tr: 'Toplam Yanıt', en: 'Total Responses' },
  responseRate: { tr: 'Yanıt Oranı', en: 'Response Rate' },
  distribution: { tr: 'Puan Dağılımı', en: 'Rating Distribution' },
  dailyResponses: { tr: 'Son 30 Gün — Günlük Yanıt', en: 'Last 30 Days — Daily Responses' },
  feedback: { tr: 'Gönderilen Geri Bildirimler', en: 'Submitted Feedback' },
  noResponses: { tr: 'Henüz yanıt yok.', en: 'No responses yet.' },
  rating: { tr: 'Puan', en: 'Rating' },
  wantsContact: { tr: 'İletişim İstiyor', en: 'Wants Contact' },
  fullName: { tr: 'Ad Soyad', en: 'Full Name' },
  company: { tr: 'Şirket', en: 'Company' },
  phone: { tr: 'Telefon', en: 'Phone' },
  note: { tr: 'Not', en: 'Note' },
  ratingChanged: {
    tr: 'E-postada {from} verildi, yanıt sayfasında {to} olarak değiştirildi',
    en: 'Rated {from} in the email, changed to {to} on the response page',
  },
  before: { tr: ' (önce {n})', en: ' (was {n})' },
  template: { tr: 'Şablon', en: 'Template' },
  scale: { tr: 'Ölçek', en: 'Scale' },
  impressions: { tr: 'Gösterim', en: 'Impressions' },
  colAction: { tr: 'Aksiyon', en: 'Actions' },
  noSurveys: { tr: 'Henüz hiçbir şablona anket eklenmedi.', en: 'No survey has been added to any template yet.' },
  points: { tr: '{n} Nokta', en: '{n} Points' },
  analytics: { tr: 'Analitik', en: 'Analytics' },
  priority: { tr: 'Öncelik', en: 'Priority' },
  owner: { tr: 'Sahip', en: 'Owner' },
  noActions: { tr: 'Henüz olumsuz geri bildirim alarmı tetiklenmedi.', en: 'No negative feedback alert has been triggered yet.' },
  unassigned: { tr: 'Atanmadı', en: 'Unassigned' },
  detail: { tr: 'Detay', en: 'Details' },
  actionTitle: { tr: '{name} — {rating}/{scale} Puan', en: '{name} — {rating}/{scale} Rating' },
  wantsContactLower: { tr: 'İletişim istiyor', en: 'Wants contact' },
  comment: { tr: 'Yorum', en: 'Comment' },
  targetDate: { tr: 'Hedef Tarih', en: 'Target Date' },
  rootCause: { tr: 'Kök Neden', en: 'Root Cause' },
  notes: { tr: 'Notlar', en: 'Notes' },
  closure: { tr: 'Kapanış Sonucu', en: 'Closure Result' },
};

function RatingDistributionBars({ distribution }) {
  const { lang } = useT(messages);
  if (!distribution || distribution.length === 0) return null;
  const max = Math.max(...distribution.map(d => d.count), 1);
  return (
    <div className="survey-distribution">
      {distribution.map(d => (
        <div className="survey-distribution-row" key={d.rating}>
          <span className="survey-distribution-emoji">{d.emoji}</span>
          <span className="survey-distribution-label">{surveyLabel(d.label, lang)}</span>
          <div className="survey-distribution-bar-track">
            <div className="survey-distribution-bar" style={{ width: `${(d.count / max) * 100}%` }} />
          </div>
          <span className="survey-distribution-count">{d.count} (%{d.percentage})</span>
        </div>
      ))}
    </div>
  );
}

function DailyResponsesChart({ series }) {
  const { t } = useT(messages);
  if (!series || series.length === 0) {
    return <p className="survey-analytics-empty">{t('noResponses30')}</p>;
  }
  const max = Math.max(...series.map(d => d.responses), 1);
  return (
    <div className="survey-bar-chart">
      {series.map(d => (
        <div className="survey-bar-col" key={d.date}>
          <div className="survey-bar" style={{ height: `${(d.responses / max) * 100}%` }} title={`${d.date}: ${d.responses}`} />
          <span className="survey-bar-label">{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

// "Sizinle iletişime geçilmesini ister misiniz?" answer from the response page.
const contactLabel = (value, t) => (value === true ? t('yes') : value === false ? t('no') : '—');

export default function Surveys() {
  const analyticsOverlayClose = useOverlayClose(() => setAnalyticsFor(null));
  const actionOverlayClose = useOverlayClose(() => setEditingAction(null));
  const { t, formatDateTime } = useT(messages);
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const [activeTab, setActiveTab] = useState('surveys');
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsFor, setAnalyticsFor] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [owners, setOwners] = useState([]);
  const [editingAction, setEditingAction] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(endpoints.signatures.surveys.list);
      setSurveys(data.results || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadActions = async () => {
    setActionsLoading(true);
    try {
      const data = await api.get(endpoints.signatures.surveyFeedbackActions.list);
      setActions(data.results || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (activeTab !== 'actions') return;
    loadActions();
    // Reuses the same org-scoped team-member list Settings > Ekip Üyeleri
    // shows — action owners are platform accounts (people who log into
    // this portal), not directory employees, so endpoints.users (Graph/
    // Workspace directory) would be the wrong list here.
    api.get(endpoints.admin.users.list)
      .then(data => setOwners(data.results || data || []))
      .catch(() => {});
  }, [activeTab]);

  const updateAction = async (id, patch) => {
    setError(null);
    try {
      const updated = await api.put(endpoints.signatures.surveyFeedbackActions.detail(id), patch);
      setActions(prev => prev.map(a => (a.id === id ? updated : a)));
      return updated;
    } catch (err) {
      setError(t('actionUpdateFailed', { msg: err.message }));
      return null;
    }
  };

  const openAnalytics = async (survey) => {
    setAnalyticsFor(survey);
    setAnalyticsLoading(true);
    setAnalytics(null);
    try {
      const data = await api.get(endpoints.signatures.surveys.analytics(survey.id));
      setAnalytics(data);
    } catch (err) {
      setError(t('analyticsFailed', { msg: err.message }));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  return (
    <div className="page-stack">
      <h1>{t('title')}</h1>
      <p className="surveys-hint">{t('hint')}</p>

      <div className="surveys-tabs">
        <Button
          variant={activeTab === 'surveys' ? 'primary' : 'secondary'}
          onClick={() => setActiveTab('surveys')}
        >
          {t('surveysTab')}
        </Button>
        <Button
          variant={activeTab === 'actions' ? 'primary' : 'secondary'}
          onClick={() => setActiveTab('actions')}
        >
          {t('actionsTab')}{actions.length > 0 ? ` (${actions.filter(a => a.status !== 'closed' && a.status !== 'resolved').length})` : ''}
        </Button>
      </div>

      {error && !analyticsFor && !editingAction && <Alert variant="error">{t('error', { msg: error })}</Alert>}

      {analyticsFor && (
        <div className="modal-overlay" {...analyticsOverlayClose}>
          <div className="modal-window survey-modal">
            <h2>{t('analyticsTitle', { name: analyticsFor.signature_template_name })}</h2>
            {error && <Alert variant="error">{error}</Alert>}
            {analyticsLoading ? (
              <div className="page-loading"><Spinner /></div>
            ) : analytics ? (
              <>
                <div className="survey-analytics-tiles">
                  <DashboardCard CardIcon="⭐" title={t('avgRating')} count={analytics.average_rating ?? '—'} />
                  <DashboardCard CardIcon="💬" title={t('totalResponses')} count={analytics.total_responses} />
                  <DashboardCard CardIcon="📈" title={t('responseRate')} count={`%${analytics.response_rate}`} />
                </div>
                <h3 className="survey-analytics-subheading">{t('distribution')}</h3>
                <RatingDistributionBars distribution={analytics.distribution} />
                <h3 className="survey-analytics-subheading">{t('dailyResponses')}</h3>
                <DailyResponsesChart series={analytics.daily_responses} />
                <h3 className="survey-analytics-subheading">{t('feedback')}</h3>
                {(analytics.recent_responses || []).length === 0 ? (
                  <p className="survey-analytics-empty">{t('noResponses')}</p>
                ) : (
                  <div className="survey-responses-table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('date')}</th>
                          <th>{t('rating')}</th>
                          <th>{t('wantsContact')}</th>
                          <th>{t('fullName')}</th>
                          <th>{t('company')}</th>
                          <th>{t('phone')}</th>
                          <th>{t('email')}</th>
                          <th>{t('note')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.recent_responses.map((r, i) => (
                          <tr key={i}>
                            <td>{formatDateTime(r.responded_at)}</td>
                            <td title={r.original_rating != null ? t('ratingChanged', { from: r.original_rating, to: r.rating }) : undefined}>
                              {r.rating}{r.original_rating != null && <span className="survey-rating-changed">{t('before', { n: r.original_rating })}</span>}
                            </td>
                            <td>{contactLabel(r.contact_requested, t)}</td>
                            <td>{r.full_name || '—'}</td>
                            <td>{r.company || '—'}</td>
                            <td>{r.phone || '—'}</td>
                            <td>{r.email || '—'}</td>
                            <td>{r.comment || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : null}
            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={() => setAnalyticsFor(null)}>{t('close')}</Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'surveys' && (
        loading ? (
          <div className="page-loading"><Spinner /></div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('template')}</th>
                <th>{t('scale')}</th>
                <th>{t('status')}</th>
                <th>{t('impressions')}</th>
                <th>{t('colAction')}</th>
              </tr>
            </thead>
            <tbody>
              {surveys.length === 0 && (
                <tr><td colSpan={5} className="data-table-empty">{t('noSurveys')}</td></tr>
              )}
              {surveys.map(s => (
                <tr key={s.id}>
                  <td>{s.signature_template_name}</td>
                  <td>{t('points', { n: s.scale })}</td>
                  <td><Badge variant={s.is_active ? 'success' : 'danger'}>{s.is_active ? t('active') : t('inactive')}</Badge></td>
                  <td>{s.impression_count}</td>
                  <td className="survey-actions">
                    <Button variant="secondary" onClick={() => openAnalytics(s)}>{t('analytics')}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {activeTab === 'actions' && (
        actionsLoading ? (
          <div className="page-loading"><Spinner /></div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('date')}</th>
                <th>{t('template')}</th>
                <th>{t('rating')}</th>
                <th>{t('status')}</th>
                <th>{t('priority')}</th>
                <th>{t('owner')}</th>
                <th>{t('colAction')}</th>
              </tr>
            </thead>
            <tbody>
              {actions.length === 0 && (
                <tr><td colSpan={7} className="data-table-empty">{t('noActions')}</td></tr>
              )}
              {actions.map(a => (
                <tr key={a.id}>
                  <td>{formatDateTime(a.response_responded_at)}</td>
                  <td>{a.response_template_name}</td>
                  <td>{a.response_rating} / {a.response_scale}</td>
                  <td>
                    <select
                      value={a.status}
                      onChange={e => updateAction(a.id, { status: e.target.value })}
                      className="survey-action-inline-select"
                    >
                      {Object.entries(t('actionStatus')).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={a.priority}
                      onChange={e => updateAction(a.id, { priority: e.target.value })}
                      className="survey-action-inline-select"
                    >
                      {Object.entries(t('actionPriority')).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={a.owner || ''}
                      onChange={e => updateAction(a.id, { owner: e.target.value || null })}
                      className="survey-action-inline-select"
                    >
                      <option value="">{t('unassigned')}</option>
                      {owners.map(o => (
                        <option key={o.id} value={o.id}>
                          {[o.first_name, o.last_name].filter(Boolean).join(' ') || o.email}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="survey-actions">
                    <Button variant="secondary" onClick={() => setEditingAction(a)}>{t('detail')}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {editingAction && (
        <div className="modal-overlay" {...actionOverlayClose}>
          <div className="modal-window survey-modal">
            <h2>{t('actionTitle', { name: editingAction.response_template_name, rating: editingAction.response_rating, scale: editingAction.response_scale })}</h2>
            {error && <Alert variant="error">{error}</Alert>}
            <div className="survey-action-response-detail">
              <p><strong>{t('fullName')}:</strong> {editingAction.response_full_name || '—'}</p>
              <p><strong>{t('wantsContactLower')}:</strong> {contactLabel(editingAction.response_contact_requested, t)}</p>
              <p><strong>{t('company')}:</strong> {editingAction.response_company || '—'}</p>
              <p><strong>{t('email')}:</strong> {editingAction.response_email || '—'}</p>
              <p><strong>{t('phone')}:</strong> {editingAction.response_phone || '—'}</p>
              <p><strong>{t('comment')}:</strong> {editingAction.response_comment || '—'}</p>
            </div>
            <div className="form-group">
              <label>{t('targetDate')}</label>
              <input
                type="date"
                value={editingAction.target_date || ''}
                onChange={e => setEditingAction({ ...editingAction, target_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>{t('rootCause')}</label>
              <input
                type="text"
                value={editingAction.root_cause || ''}
                onChange={e => setEditingAction({ ...editingAction, root_cause: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>{t('notes')}</label>
              <textarea
                rows={3}
                value={editingAction.notes || ''}
                onChange={e => setEditingAction({ ...editingAction, notes: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>{t('closure')}</label>
              <textarea
                rows={3}
                value={editingAction.closure_result || ''}
                onChange={e => setEditingAction({ ...editingAction, closure_result: e.target.value })}
              />
            </div>
            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={() => setEditingAction(null)}>{t('close')}</Button>
              <Button
                type="button"
                onClick={async () => {
                  const updated = await updateAction(editingAction.id, {
                    target_date: editingAction.target_date || null,
                    root_cause: editingAction.root_cause,
                    notes: editingAction.notes,
                    closure_result: editingAction.closure_result,
                  });
                  if (updated) setEditingAction(null);
                }}
              >
                {t('save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
